import { Component, OnDestroy, inject, signal } from '@angular/core';
import { DatePipe, DecimalPipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { CardModule } from 'primeng/card';
import { ButtonModule } from 'primeng/button';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { DialogModule } from 'primeng/dialog';
import { MessageModule } from 'primeng/message';
import { SelectModule } from 'primeng/select';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { InputTextModule } from 'primeng/inputtext';
import { TabsModule } from 'primeng/tabs';
import { TooltipModule } from 'primeng/tooltip';
import { MiddlewareApi } from '../../core/services/middleware-api.service';
import { OhlqIngestResult, OhlqItemDetail, OhlqProduct, OhlqSyncRun, OhlqTimelineEntry } from '../../core/models';
import { describeOhlqEvent, visibleOhlqTimeline } from './ohlq-events';

// Ohio's statewide catalogue, read out of the event-sourced OHLQ ledger.
//
// Read-only, like the product catalog: the ledger records what the state published and what we observed of
// it. The one write is the ingest button, which asks the middleware to read both OHLQ feeds again — and
// because every item is hashed, re-reading an unchanged catalogue writes nothing. That is why the run
// summary leads with "unchanged": it is the number that says the ledger is behaving.
@Component({
    selector: 'app-ohlq-catalog',
    standalone: true,
    imports: [
        CardModule, ButtonModule, TableModule, TagModule, DialogModule, MessageModule,
        SelectModule, ToggleSwitchModule, InputTextModule, TabsModule, TooltipModule, FormsModule, DecimalPipe, DatePipe
    ],
    template: `
        <p-card>
            <div class="flex flex-wrap items-start justify-between gap-3 mb-4">
                <div>
                    <h5 class="mb-1">Ohio catalog<i class="pi pi-info-circle text-surface-400 ml-2 cursor-help" pTooltip="The state's price list as last read. Ohio sets the prices, so they are the same for every merchant." tooltipPosition="top"></i></h5>
                </div>
                <div class="flex gap-2">
                    <p-button icon="pi pi-cloud-download" label="Read OHLQ feed" size="small" severity="secondary"
                              [loading]="ingesting()" (onClick)="ingest()" />
                    <p-button icon="pi pi-upload" label="Publish to Magento" size="small" severity="secondary"
                              [loading]="publishStarting()" (onClick)="publish()" />
                    <p-button icon="pi pi-send" label="Send to store" size="small" severity="secondary"
                              (onClick)="toggleStoreSend()" />
                    <p-button icon="pi pi-images" label="Heal images" size="small" severity="secondary"
                              [loading]="healRunning()" (onClick)="healGallery()" />
                </div>
            </div>

            @if (healNote()) {
                <p class="text-sm text-surface-500 mb-3 mt-0">{{ healNote() }}</p>
            }

            @if (publishStarted(); as id) {
                <p-message severity="info" styleClass="mb-3">
                    @if (publishJoined()) {
                        A publish is already running — showing it under Syncs.
                    } @else {
                        Publish run started — watch it under Syncs. It keeps going even if you leave this page.
                    }
                </p-message>
            }

            @if (ingestResult(); as r) {
                <p-message severity="success" styleClass="mb-3">
                    Read {{ r.productRows | number }} product rows and {{ r.priceRows | number }} price rows
                    over {{ r.items | number }} items — <strong>{{ r.unchanged | number }} unchanged</strong>,
                    {{ r.created | number }} new, {{ r.changed | number }} changed@if (r.failed) {, {{ r.failed | number }} failed}.
                </p-message>
            }
            @if (ingestError(); as e) { <p-message severity="error" styleClass="mb-3">{{ e }}</p-message> }
            @if (error(); as e) { <p-message severity="error" styleClass="mb-3">{{ e }}</p-message> }

            <p-tabs [value]="activeTab()" (valueChange)="onTabChange($event)">
                <p-tablist>
                    <p-tab value="catalog">Catalog</p-tab>
                    <p-tab value="syncs">Syncs</p-tab>
                </p-tablist>
                <p-tabpanels>
                    <p-tabpanel value="catalog">

            <!-- Summary. Counts come from the same indexed queries the grid uses, so the header cannot
                 disagree with the rows underneath it. -->
            <div class="flex flex-wrap gap-6 mb-4 pb-4 border-b border-surface-200 dark:border-surface-700">
                <div>
                    <div class="text-2xl font-semibold tabular-nums">{{ totalProducts() | number }}</div>
                    <div class="text-xs uppercase tracking-wide text-surface-500">products</div>
                </div>
                <div>
                    <div class="text-2xl font-semibold tabular-nums">{{ totalMultiSize() | number }}</div>
                    <div class="text-xs uppercase tracking-wide text-surface-500">sold in several sizes</div>
                </div>
                <div>
                    <div class="text-2xl font-semibold tabular-nums">{{ totalAll() | number }}</div>
                    <div class="text-xs uppercase tracking-wide text-surface-500">bottles</div>
                </div>
                <div>
                    <div class="text-2xl font-semibold tabular-nums text-green-600">{{ totalSellable() | number }}</div>
                    <div class="text-xs uppercase tracking-wide text-surface-500">sellable</div>
                </div>
                <div>
                    <div class="text-2xl font-semibold tabular-nums">{{ totalIncomplete() | number }}</div>
                    <div class="text-xs uppercase tracking-wide text-surface-500">missing price or details</div>
                </div>
            </div>

            <div class="flex flex-wrap items-center gap-2 mb-3">
                <input pInputText [(ngModel)]="search" (keyup.enter)="reload()" placeholder="Search item id / name" class="w-72" />
                <p-select [options]="categoryOptions()" [(ngModel)]="category" (onChange)="reload()"
                          placeholder="All categories" [showClear]="true" [filter]="true" styleClass="w-56" />
                <p-select [options]="coverageOptions" [(ngModel)]="coverage" (onChange)="reload()"
                          placeholder="Any coverage" [showClear]="true" styleClass="w-56" />
                <div class="flex items-center gap-2">
                    <p-toggleswitch [(ngModel)]="visibleOnly" (onChange)="reload()" inputId="ohlqVisible" />
                    <label for="ohlqVisible" class="text-sm">Sellable only</label>
                </div>
                <div class="flex items-center gap-2">
                    <p-toggleswitch [(ngModel)]="multiSizeOnly" (onChange)="reload()" inputId="ohlqMulti" />
                    <label for="ohlqMulti" class="text-sm">Multi-size only</label>
                </div>
                <p-button icon="pi pi-search" label="Search" size="small" (onClick)="reload()" />
                <p-button icon="pi pi-refresh" [text]="true" size="small" (onClick)="reload()" />
            </div>

            <!-- Send a synthetic assortment to ONE merchant store. Items sends exactly those ohio ids;
                 Sample sends a stable pseudo-random shelf; both empty refreshes what the store already
                 carries (delta'd server-side). The run lands in the sync ledger below. -->
            @if (showStoreSend) {
                <div class="mb-4 p-3 border border-surface-200 dark:border-surface-700 rounded-lg flex flex-wrap items-end gap-3">
                    <div class="flex flex-col gap-1">
                        <label class="text-xs uppercase tracking-wide text-surface-500" for="sendStore">Merchant store</label>
                        <p-select inputId="sendStore" [options]="storeOptions()" [(ngModel)]="sendLocation"
                                  [loading]="storesLoading()" placeholder="Pick a merchant" [filter]="true"
                                  styleClass="w-64" />
                    </div>
                    <div class="flex flex-col gap-1 grow" style="min-width: 16rem">
                        <label class="text-xs uppercase tracking-wide text-surface-500" for="sendItems">
                            Items<i class="pi pi-info-circle text-surface-400 ml-2 cursor-help" pTooltip="Comma-separated Ohio item IDs. Leave empty to refresh what the store already carries." tooltipPosition="top"></i>
                        </label>
                        <input pInputText id="sendItems" [(ngModel)]="sendSkus" placeholder="3907L,3907B" />
                    </div>
                    <div class="flex flex-col gap-1">
                        <label class="text-xs uppercase tracking-wide text-surface-500" for="sendCount">Sample</label>
                        <input pInputText id="sendCount" type="number" [(ngModel)]="sendCount" placeholder="25"
                               class="w-24" [disabled]="sendAll" />
                    </div>
                    <div class="flex items-center gap-2 pb-2">
                        <p-toggleswitch [(ngModel)]="sendAll" inputId="sendAll" />
                        <label for="sendAll" class="text-sm"
                               title="Sends every sellable, priced item. This can take a long time.">
                            Whole catalog
                        </label>
                    </div>
                    <p-button icon="pi pi-send" label="Send" size="small"
                              [loading]="storeSendStarting()" (onClick)="sendToStore()" />
                    @if (storeSendNote()) {
                        <span class="text-sm text-surface-500 pb-2">{{ storeSendNote() }}</span>
                    }
                </div>
            }


            <!-- ONE LIST, two kinds of row that are really one kind: a product. Clicking a row opens that
                 product — the sizes live in the detail, where each one can be inspected, rather than in an
                 inline drawer that only ever showed the same summary twice. -->
            <p-table [value]="items()" [loading]="loading()" [lazy]="true" [paginator]="true" [rows]="pageSize"
                     [totalRecords]="total()" (onLazyLoad)="onPage($event)" [first]="first()"
                     [rowHover]="true" styleClass="p-datatable-sm">
                <ng-template pTemplate="header">
                    <tr>
                        <th style="width:3.5rem"></th>
                        <th pSortableColumn="name">Product <p-sortIcon field="name" /></th>
                        <th>Category</th>
                        <th pSortableColumn="sizes" class="whitespace-nowrap">Sizes <p-sortIcon field="sizes" /></th>
                        <th pSortableColumn="price" class="text-right">Price <p-sortIcon field="price" /></th>
                        <th>Status</th>
                        <th pSortableColumn="lastChanged" class="whitespace-nowrap">Changed <p-sortIcon field="lastChanged" /></th>
                    </tr>
                </ng-template>

                <ng-template pTemplate="body" let-p>
                    <tr class="cursor-pointer" (click)="rowClick(p)">
                        <td>
                            @if (p.imageThumb) {
                                <img [src]="p.imageThumb" [alt]="p.name" class="h-9 w-9 object-contain" />
                            } @else {
                                <div class="h-9 w-9 rounded bg-surface-100 dark:bg-surface-800 flex items-center justify-center">
                                    <i class="pi pi-image text-surface-300 text-xs"></i>
                                </div>
                            }
                        </td>
                        <td>
                            <div class="text-sm font-medium">{{ p.name || '—' }}</div>
                            <div class="text-xs text-surface-500">
                                @if (p.sizeCount === 1) {
                                    <span class="font-mono">{{ p.sizes[0].itemId }}</span>
                                    @if (p.sizes[0].volume) { · {{ p.sizes[0].volume }} }
                                } @else {
                                    {{ sizeList(p) }}
                                }
                            </div>
                        </td>
                        <td class="text-sm">{{ p.category || '—' }}{{ p.type ? ' · ' + p.type : '' }}</td>
                        <td class="text-sm whitespace-nowrap">
                            @if (p.sizeCount > 1) {
                                <p-tag [value]="p.sizeCount + ' sizes'" severity="info" />
                            } @else {
                                <span class="text-surface-400">single</span>
                            }
                        </td>
                        <td class="text-sm text-right tabular-nums whitespace-nowrap">
                            {{ p.minPrice | number: '1.2-2' }}
                            @if (p.maxPrice !== p.minPrice) {
                                <span class="text-surface-500">– {{ p.maxPrice | number: '1.2-2' }}</span>
                            }
                        </td>
                        <td class="whitespace-nowrap">
                            @if (p.visibleCount === 0) {
                                <p-tag value="Hidden" severity="secondary" />
                            } @else if (p.visibleCount === p.sizeCount) {
                                <p-tag value="Sellable" severity="success" />
                            } @else {
                                <p-tag [value]="p.visibleCount + ' of ' + p.sizeCount + ' sellable'" severity="warn" />
                            }
                            @if (p.scheduledCount > 0) {
                                <p-tag icon="pi pi-clock" [value]="p.scheduledCount > 1 ? p.scheduledCount + ' price changes due' : 'Price change due'"
                                       severity="warn" styleClass="ml-1" />
                            }
                        </td>
                        <td class="text-sm text-surface-500 whitespace-nowrap">{{ p.lastChangedAt | date: 'short' }}</td>
                    </tr>
                </ng-template>

                <ng-template pTemplate="emptymessage">
                    <tr>
                        <td colspan="7" class="text-surface-500">
                            @if (totalAll()) {
                                Nothing matches these filters.
                            } @else {
                                Nothing here yet — press “Read OHLQ feed” to pull the state catalogue for the first time.
                            }
                        </td>
                    </tr>
                </ng-template>
            </p-table>
                    </p-tabpanel>

                    <!-- The sync ledger, ALONE: every ingest and every publish is a row from the
                         moment it starts, live counters, retry on the failed. Polls only while this
                         tab is open and something is Running. -->
                    <p-tabpanel value="syncs">
                        <div class="flex justify-end mb-2">
                            <p-button icon="pi pi-refresh" label="Refresh" size="small" [text]="true"
                                      (onClick)="refreshSyncs()" />
                        </div>
                        <p-table [value]="syncs()" styleClass="p-datatable-sm">
                            <ng-template pTemplate="header">
                                <tr>
                                    <th>Sync</th><th>Started</th><th style="min-width:11rem">Progress</th>
                                    <th>Result</th><th>Status</th>
                                </tr>
                            </ng-template>
                            <ng-template pTemplate="body" let-r>
                                <tr>
                                    <td class="text-sm whitespace-nowrap">
                                        {{ kindLabel(r) }}
                                        @if (r.scope) { <span class="text-xs text-surface-500">({{ r.scope }})</span> }
                                    </td>
                                    <td class="text-sm text-surface-500 whitespace-nowrap">{{ r.startedAt | date: 'short' }}</td>
                                    <td>
                                        @if (r.totalItems > 0) {
                                            <div class="flex items-center gap-2">
                                                <div class="grow h-2 rounded bg-surface-200 dark:bg-surface-700 overflow-hidden">
                                                    <div class="h-full bg-primary transition-all"
                                                         [style.width.%]="(100 * r.processedItems / r.totalItems)"></div>
                                                </div>
                                                <span class="text-xs tabular-nums whitespace-nowrap">
                                                    {{ r.processedItems | number }}/{{ r.totalItems | number }}
                                                </span>
                                            </div>
                                        } @else {
                                            <span class="text-xs text-surface-500">{{ r.processedItems | number }} items</span>
                                        }
                                    </td>
                                    <td class="text-xs text-surface-600 dark:text-surface-300">
                                        @if (r.kind === 'Ingest') {
                                            {{ r.changed | number }} changed · {{ r.unchanged | number }} unchanged
                                            @if (r.created) { · {{ r.created | number }} new }
                                        } @else if (r.kind.startsWith('PublishStore:')) {
                                            <!-- A store send is two phases: publish the product events, then stock
                                                 each product as the pipeline lands it. Named separately, so the
                                                 progress bar's arithmetic never needs decoding. -->
                                            {{ r.pushed | number }} published · {{ (r.processedItems - r.pushed < 0 ? 0 : r.processedItems - r.pushed) | number }} stocked
                                        } @else {
                                            {{ r.pushed | number }} pushed
                                            @if (r.familiesAssembled) { · {{ r.familiesAssembled }} families }
                                        }
                                        @if (r.failed) { <span class="text-red-600">· {{ r.failed }} failed</span> }
                                    </td>
                                    <td>
                                        <div class="flex items-center gap-2">
                                            <p-tag [value]="statusOf(r)" [severity]="statusSeverity(r)" />
                                            @if (statusOf(r) === 'Failed' || statusOf(r) === 'Stalled') {
                                                <p-button icon="pi pi-replay" size="small" [text]="true"
                                                          [loading]="retryingId() === r.id" (onClick)="retryRun(r)" />
                                            }
                                        </div>
                                    </td>
                                </tr>
                            </ng-template>
                            <ng-template pTemplate="emptymessage">
                                <tr><td colspan="5" class="text-surface-500 text-sm">No syncs recorded yet.</td></tr>
                            </ng-template>
                        </p-table>
                    </p-tabpanel>
                </p-tabpanels>
            </p-tabs>
        </p-card>

        <p-dialog [(visible)]="showDetail" [modal]="true" [style]="{ width: '56rem' }"
                  [header]="detail()?.state?.name || selectedItemId() || 'Item'" (onHide)="closeDetail()">
            @if (detailLoading()) {
                <p class="text-sm text-surface-500">Loading…</p>
            } @else if (detailError(); as e) {
                <p-message severity="error">{{ e }}</p-message>
            } @else if (detail(); as d) {
                <div class="flex gap-6 mb-5 transition-opacity" [class.opacity-50]="switching()">
                    <div class="shrink-0 w-32 h-40 rounded-lg bg-surface-50 dark:bg-surface-800 flex items-center justify-center p-2">
                        @if (d.state.imageFront) {
                            <img [src]="d.state.imageFront" [alt]="d.state.name" class="max-h-full max-w-full object-contain" />
                        } @else {
                            <i class="pi pi-image text-3xl text-surface-300"></i>
                        }
                    </div>

                    <div class="grow min-w-0">
                        <!-- The price leads: it is the number the whole feed exists to publish. -->
                        <div class="flex items-baseline gap-3 flex-wrap mb-1">
                            <span class="text-3xl font-semibold tabular-nums">{{ d.state.currentPrice | number: '1.2-2' }}</span>
                            @if (d.state.currentEffectiveDate) {
                                <span class="text-sm text-surface-500">since {{ d.state.currentEffectiveDate | date: 'mediumDate' }}</span>
                            }
                            <p-tag [value]="d.state.websiteVisible ? 'Sellable' : 'Hidden'"
                                   [severity]="d.state.websiteVisible ? 'success' : 'secondary'" />
                            @if (d.state.bottleLimit > 0) {
                                <p-tag [value]="d.state.bottleLimit + ' per order'" severity="warn" />
                            }
                        </div>

                        @if (d.state.nextEffectiveDate && d.state.nextPrice !== d.state.currentPrice) {
                            <!-- The fact only this ledger can give you: a price change that has not happened yet. -->
                            <div class="inline-flex items-center gap-2 text-sm rounded px-2 py-1 mb-3
                                        bg-amber-50 dark:bg-amber-950 text-amber-800 dark:text-amber-200">
                                <i class="pi pi-clock text-xs"></i>
                                <span>Changes to <strong class="tabular-nums">{{ d.state.nextPrice | number: '1.2-2' }}</strong>
                                      on {{ d.state.nextEffectiveDate | date: 'mediumDate' }}</span>
                            </div>
                        }

                        <dl class="grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-3 mt-3">
                            <div>
                                <dt class="text-xs uppercase tracking-wide text-surface-500">Item id</dt>
                                <dd class="text-sm font-mono m-0">{{ d.itemId }}</dd>
                            </div>
                            <div>
                                <dt class="text-xs uppercase tracking-wide text-surface-500">Size</dt>
                                <dd class="text-sm m-0">{{ d.state.volume || '—' }}@if (d.state.proof) { · {{ d.state.proof | number: '1.0-1' }} proof}</dd>
                            </div>
                            <div>
                                <dt class="text-xs uppercase tracking-wide text-surface-500">Category</dt>
                                <dd class="text-sm m-0">{{ d.state.category || '—' }}{{ d.state.type ? ' · ' + d.state.type : '' }}</dd>
                            </div>
                            <div class="min-w-0">
                                <dt class="text-xs uppercase tracking-wide text-surface-500">Barcode</dt>
                                <dd class="text-sm font-mono m-0 truncate">{{ d.state.upc || '—' }}</dd>
                                @if (d.state.upcCount > 1) {
                                    <dd class="text-xs text-surface-500 m-0">+{{ d.state.upcCount - 1 }} more on file</dd>
                                }
                            </div>
                            <div>
                                <dt class="text-xs uppercase tracking-wide text-surface-500">State status</dt>
                                <dd class="text-sm m-0">{{ d.state.salesStatusCode || '—' }}</dd>
                            </div>
                            <div>
                                <dt class="text-xs uppercase tracking-wide text-surface-500">Known from</dt>
                                <dd class="text-sm m-0">{{ coverageLabel(d.state.coverage) }}</dd>
                            </div>
                        </dl>
                    </div>
                </div>

                <!-- The family. A bottle is rarely sold in one size, and until this existed the four
                     Paramount Vodkas were four unrelated rows. -->
                @if (d.family.length > 1) {
                    <div class="flex items-baseline justify-between mb-2">
                        <h6 class="m-0">Sizes</h6>
                        <span class="text-xs text-surface-500">
                            {{ d.family.length }} sizes
                        </span>
                    </div>
                    <div class="flex flex-wrap gap-2 mb-5">
                        @for (m of d.family; track m.itemId) {
                            <!-- Selected state comes from the CLICK (selectedItemId), not from the loaded
                                 payload, so the picker answers immediately instead of after a round trip. -->
                            <button type="button" (click)="switchSize(m.itemId)"
                                    class="px-3 py-2 rounded-lg border text-left transition-colors min-w-24"
                                    [class.border-primary]="m.itemId === selectedItemId()"
                                    [class.ring-1]="m.itemId === selectedItemId()"
                                    [class.ring-primary]="m.itemId === selectedItemId()"
                                    [class.bg-primary-50]="m.itemId === selectedItemId()"
                                    [class.dark:bg-primary-950]="m.itemId === selectedItemId()"
                                    [class.border-surface-200]="m.itemId !== selectedItemId()"
                                    [class.dark:border-surface-700]="m.itemId !== selectedItemId()"
                                    [class.hover:border-surface-400]="m.itemId !== selectedItemId()">
                                <div class="text-sm font-medium">{{ m.volume || m.itemId }}</div>
                                <div class="text-xs tabular-nums text-surface-500">
                                    {{ m.currentPrice | number: '1.2-2' }}
                                    @if (!m.websiteVisible) { <span class="text-surface-400">· hidden</span> }
                                </div>
                            </button>
                        }
                    </div>
                }

                @if (d.consecutiveMissingCount > 0) {
                    <p-message severity="warn" styleClass="mb-3">
                        Absent from the last {{ d.consecutiveMissingCount }} feed read(s), most recently
                        {{ d.lastMissingAt | date: 'short' }}.
                    </p-message>
                }

                <div class="flex items-baseline justify-between mb-2">
                    <h6 class="m-0">History</h6>
                    <span class="text-xs text-surface-500">
                        first seen {{ d.firstSeenAt | date: 'short' }}
                    </span>
                </div>
                <p-table [value]="timeline()" styleClass="p-datatable-sm" [class.opacity-50]="switching()">
                    <ng-template pTemplate="header">
                        <tr><th style="width:4rem">#</th><th>What changed</th><th style="width:11rem">When</th></tr>
                    </ng-template>
                    <ng-template pTemplate="body" let-e>
                        <tr>
                            <td class="text-xs text-surface-500 tabular-nums">{{ e.version }}</td>
                            <td class="text-sm">{{ describe(e) }}</td>
                            <td class="text-sm text-surface-500 whitespace-nowrap">{{ e.occurredAt | date: 'short' }}</td>
                        </tr>
                    </ng-template>
                    <ng-template pTemplate="emptymessage">
                        <tr><td colspan="3" class="text-surface-500">Nothing recorded yet.</td></tr>
                    </ng-template>
                </p-table>
            }
        </p-dialog>
    `
})
export class OhlqCatalog implements OnDestroy {
    private readonly api = inject(MiddlewareApi);

    readonly items = signal<OhlqProduct[]>([]);
    readonly total = signal(0);
    readonly first = signal(0);
    readonly loading = signal(true);
    readonly error = signal<string | null>(null);
    readonly pageSize = 50;

    // Summary counts, independent of the grid's filters. Products and bottles are BOTH shown because they
    // are different numbers — 7,491 bottles arrive as roughly 5,800 products — and a header that showed
    // only one of them next to a grid paging the other would be a quiet lie.
    readonly totalProducts = signal(0);
    readonly totalMultiSize = signal(0);
    readonly totalAll = signal(0);
    readonly totalSellable = signal(0);
    readonly totalIncomplete = signal(0);

    search = '';
    category: string | null = null;
    coverage: string | null = null;
    visibleOnly = false;
    multiSizeOnly = false;

    // Echoed to the server. A plain field: only the loader reads it, and it re-runs on every table event.
    private sort: string | null = null;

    readonly categoryOptions = signal<{ label: string; value: string }[]>([]);

    // Coverage is a closed set the middleware defines, so it is listed here rather than fetched.
    readonly coverageOptions = [
        { label: 'Priced and described', value: 'Both' },
        { label: 'Priced, no details', value: 'PriceOnly' },
        { label: 'Described, no price', value: 'ProductOnly' }
    ];

    readonly ingesting = signal(false);
    readonly ingestResult = signal<OhlqIngestResult | null>(null);
    readonly ingestError = signal<string | null>(null);

    // The sync ledger lives in its OWN TAB. Polls only while something is Running and the tab is
    // the active one — the sole timer on this page, cleared the moment either condition drops.
    readonly activeTab = signal<string>('catalog');
    readonly syncs = signal<OhlqSyncRun[]>([]);
    readonly publishStarting = signal(false);
    readonly publishStarted = signal<string | null>(null);
    readonly publishJoined = signal(false);
    private syncTimer: ReturnType<typeof setInterval> | null = null;

    // The per-store send panel — the mimic lever: any registered merchant store can be handed a
    // synthetic assortment referencing central items. Progress reports into the same sync ledger.
    showStoreSend = false;
    sendLocation: string | null = null;
    sendSkus = '';
    sendCount: number | null = null;
    sendAll = false;
    readonly storeSendStarting = signal(false);
    readonly storeSendNote = signal<string | null>(null);
    // The picker: onboarded merchant locations only — the same rows the product pipeline routes
    // events by, so the picker can only offer targets a send will actually reach.
    readonly storeOptions = signal<{ label: string; value: string }[]>([]);
    readonly storesLoading = signal(false);

    // The gallery heal button — one visible image per item, idempotent, answers in seconds.
    readonly healRunning = signal(false);
    readonly healNote = signal<string | null>(null);

    showDetail = false;
    readonly selectedItemId = signal<string | null>(null);
    readonly detail = signal<OhlqItemDetail | null>(null);
    readonly detailLoading = signal(false);
    /// Loading ANOTHER size of the product already on screen — dims the panel instead of replacing it.
    readonly switching = signal(false);
    readonly detailError = signal<string | null>(null);
    // The timeline WITHOUT the state carrier, computed when the detail lands rather than filtered in the
    // template on every change-detection pass.
    readonly timeline = signal<OhlqTimelineEntry[]>([]);

    constructor() {
        this.loadCategories();
        this.loadSummary();
    }

    private loadCategories(): void {
        this.api.getOhlqCategories().subscribe({
            next: (c) => this.categoryOptions.set(c.map((x) => ({ label: x, value: x }))),
            error: () => this.categoryOptions.set([])
        });
    }

    // Three counting queries rather than a stats endpoint: each is an indexed count the grid already
    // relies on, and inventing an aggregate route for three numbers would be the heavier answer.
    private loadSummary(): void {
        this.api.getOhlqProducts({ pageSize: 1 }).subscribe({
            next: (r) => this.totalProducts.set(r.totalItems), error: () => {}
        });
        this.api.getOhlqProducts({ pageSize: 1, multiSizeOnly: true }).subscribe({
            next: (r) => this.totalMultiSize.set(r.totalItems), error: () => {}
        });
        this.api.getOhlqCatalog({ pageSize: 1 }).subscribe({
            next: (r) => this.totalAll.set(r.totalItems), error: () => {}
        });
        this.api.getOhlqCatalog({ pageSize: 1, visibleOnly: true }).subscribe({
            next: (r) => this.totalSellable.set(r.totalItems), error: () => {}
        });
        this.api.getOhlqCatalog({ pageSize: 1, coverage: 'PriceOnly' }).subscribe({
            next: (r) => {
                const priceOnly = r.totalItems;
                this.api.getOhlqCatalog({ pageSize: 1, coverage: 'ProductOnly' }).subscribe({
                    next: (p) => this.totalIncomplete.set(priceOnly + p.totalItems), error: () => {}
                });
            },
            error: () => {}
        });
    }

    onPage(e: { first?: number | null; rows?: number | null; sortField?: string | string[] | null; sortOrder?: number | null }): void {
        // The [lazy] table reports paging AND sorting through this one event. PrimeNG types sortField as
        // string|string[] for multi-sort; this grid is single-sort, so collapse to the first key.
        this.sort = Array.isArray(e.sortField) ? (e.sortField[0] ?? null) : (e.sortField ?? null);
        const first = e.first ?? 0;
        this.first.set(first);
        this.load(Math.floor(first / this.pageSize) + 1);
    }

    reload(): void {
        this.first.set(0);
        this.load(1);
    }

    private load(page: number): void {
        this.loading.set(true);
        this.error.set(null);
        this.api.getOhlqProducts({
            search: this.search || undefined,
            category: this.category || undefined,
            visibleOnly: this.visibleOnly,
            multiSizeOnly: this.multiSizeOnly,
            sort: this.sort || undefined,
            page,
            pageSize: this.pageSize
        }).subscribe({
            next: (r) => {
                this.items.set(r.items ?? []);
                this.total.set(r.totalItems ?? 0);
                this.loading.set(false);
            },
            error: (e) => {
                this.error.set(e?.error?.detail || e?.message || 'Could not load the Ohio catalog.');
                this.items.set([]);
                this.loading.set(false);
            }
        });
    }

    /// Opens the product. The dialog IS the configurable view — it carries every size and lets one be
    /// picked — so a multi-size product opens on its cheapest bottle and the rest are one click away,
    /// rather than the row expanding into a summary the detail then repeats.
    rowClick(product: OhlqProduct): void {
        if (product.sizes.length) this.openById(product.sizes[0].itemId);
    }

    /// The sizes as a single readable line, for the collapsed row.
    sizeList(product: OhlqProduct): string {
        const volumes = product.sizes.map((s) => s.volume).filter((v) => !!v);
        return volumes.length ? volumes.join(' · ') : `${product.sizeCount} items`;
    }

    onTabChange(tab: string | number | undefined): void {
        if (tab == null) return;
        this.activeTab.set(String(tab));
        if (this.activeTab() === 'syncs') this.refreshSyncs();
        else this.stopSyncPolling();
    }

    toggleStoreSend(): void {
        this.showStoreSend = !this.showStoreSend;
        if (this.showStoreSend && !this.storeOptions().length) this.loadStores();
    }

    /// One visible image per item: front shown, thumbnail hidden. Idempotent — a healed catalog
    /// answers with zeros, and saying so beats a silent success.
    healGallery(): void {
        this.healRunning.set(true);
        this.healNote.set(null);
        this.api.healOhlqGallery().subscribe({
            next: (r) => {
                this.healRunning.set(false);
                this.healNote.set(r.shown === 0 && r.hidden === 0
                    ? 'Galleries already healthy — nothing to fix.'
                    : `Galleries healed: ${r.shown} image(s) made visible, ${r.hidden} hidden (thumbnails and duplicates).`);
            },
            error: (e) => {
                this.healRunning.set(false);
                this.healNote.set(e?.error?.detail || e?.message || 'The heal could not be run.');
            }
        });
    }

    private loadStores(): void {
        this.storesLoading.set(true);
        this.api.getOhlqStores().subscribe({
            next: (r) => {
                this.storesLoading.set(false);
                this.storeOptions.set((r.stores ?? []).map((s) => ({
                    label: `${s.code} — ${s.locationId}`,
                    value: s.locationId
                })));
                if (!this.storeOptions().length) {
                    this.storeSendNote.set('No onboarded merchants found — register a merchant store first.');
                }
            },
            error: (e) => {
                this.storesLoading.set(false);
                this.storeSendNote.set(e?.error?.detail || e?.message || 'The merchant list could not be loaded.');
            }
        });
    }

    /// Starts a per-store send. The server single-flights per store, so pressing twice joins the run
    /// already going; either way progress lives in the sync ledger, which opens itself.
    sendToStore(): void {
        const location = this.sendLocation;
        if (!location) {
            this.storeSendNote.set('Pick the merchant store to send to.');
            return;
        }
        this.storeSendStarting.set(true);
        this.storeSendNote.set(null);
        this.api.publishOhlqToStore({
            location,
            skus: this.sendSkus.trim() || undefined,
            all: this.sendAll || undefined,
            count: this.sendAll ? undefined : (this.sendCount ? Number(this.sendCount) : undefined)
        }).subscribe({
            next: (r) => {
                this.storeSendStarting.set(false);
                this.storeSendNote.set(r.alreadyRunning
                    ? 'A send for this store is already running — joined it.'
                    : 'Send started — watch it in the Syncs tab.');
                this.activeTab.set('syncs');
                this.refreshSyncs();
            },
            error: (e) => {
                this.storeSendStarting.set(false);
                this.storeSendNote.set(e?.error?.detail || e?.message || 'The send could not be started.');
            }
        });
    }

    readonly retryingId = signal<string | null>(null);

    /// CONTINUING A FAILED SYNC IS RE-RUNNING IT: every step is an idempotent upsert (stable skus,
    /// event dedupe, stock catch-up), so a re-run skips what landed and fills what is missing. The
    /// retry reconstructs the run's own scope — same kind, same parameters — and the server's
    /// single-flight makes a double press join rather than race.
    retryRun(run: OhlqSyncRun): void {
        this.retryingId.set(run.id);
        const scope = run.scope ?? '';
        const token = (name: string): string | undefined => {
            const m = scope.match(new RegExp(name + '=([^\\s]+)'));
            return m ? m[1] : undefined;
        };

        const done = (note: string) => {
            this.retryingId.set(null);
            this.storeSendNote.set(note);
            this.refreshSyncs();
        };
        const fail = (e: unknown) => {
            this.retryingId.set(null);
            const err = e as { error?: { detail?: string }; message?: string };
            this.storeSendNote.set(err?.error?.detail || err?.message || 'The retry could not be started.');
        };

        if (run.kind.startsWith('PublishStore:')) {
            this.api.publishOhlqToStore({
                location: run.kind.slice('PublishStore:'.length),
                skus: token('skus'),
                all: scope.includes('whole-catalog') || undefined,
                count: token('count') ? Number(token('count')) : undefined,
                full: scope.includes(' full') || scope.endsWith('full') || undefined
            }).subscribe({ next: () => done('Retry started — the run continues from wherever it stopped.'), error: fail });
            return;
        }
        if (run.kind === 'PublishMagento') {
            this.api.publishOhlqToMagento({
                category: token('category'),
                limit: token('limit') ? Number(token('limit')) : undefined,
                full: scope.includes('full') || undefined
            }).subscribe({ next: () => done('Retry started — the publish continues from wherever it stopped.'), error: fail });
            return;
        }
        // An ingest retry is just another ingest.
        this.api.ingestOhlqCatalog().subscribe({ next: () => done('Ingest re-run started.'), error: fail });
    }

    /// Sync rows carry the location inside the kind (PublishStore:loc-123) — unpack it for reading.
    kindLabel(run: OhlqSyncRun): string {
        if (run.kind === 'Ingest') return 'Feed read';
        if (run.kind === 'PublishMagento') return 'Magento publish';
        if (run.kind.startsWith('PublishStore:')) return `Store send (${run.kind.slice('PublishStore:'.length)})`;
        return run.kind;
    }

    publish(): void {
        this.publishStarting.set(true);
        this.publishStarted.set(null);
        this.publishJoined.set(false);
        this.api.publishOhlqToMagento().subscribe({
            next: (r) => {
                this.publishStarting.set(false);
                this.publishStarted.set(r.runId);
                this.publishJoined.set(!!r.alreadyRunning);
                this.activeTab.set('syncs');
                this.refreshSyncs();
            },
            error: (e) => {
                this.publishStarting.set(false);
                this.ingestError.set(e?.error?.detail || e?.message || 'The publish could not be started.');
            }
        });
    }

    refreshSyncs(): void {
        this.api.getOhlqSyncs().subscribe({
            next: (runs) => {
                this.syncs.set(runs);
                // Keep polling exactly as long as something is actually running and the tab is open.
                const anyRunning = runs.some((r) => r.status === 'Running');
                if (anyRunning && this.activeTab() === 'syncs') this.startSyncPolling();
                else this.stopSyncPolling();
            },
            error: () => this.stopSyncPolling()
        });
    }

    private startSyncPolling(): void {
        if (this.syncTimer !== null) return;
        this.syncTimer = setInterval(() => this.refreshSyncs(), 5000);
    }

    private stopSyncPolling(): void {
        if (this.syncTimer !== null) { clearInterval(this.syncTimer); this.syncTimer = null; }
    }

    ngOnDestroy(): void {
        this.stopSyncPolling();
    }

    /// A run that says Running but whose heartbeat is minutes old died with its host — say so instead
    /// of showing a spinner forever. Six minutes, matching the server's staleness window: the worker
    /// beats about once a minute, so anything past this is genuinely gone, not merely busy.
    statusOf(run: OhlqSyncRun): string {
        if (run.status !== 'Running') return run.status;
        const quietMs = Date.now() - new Date(run.lastProgressAt).getTime();
        return quietMs > 6 * 60_000 ? 'Stalled' : 'Running';
    }

    statusSeverity(run: OhlqSyncRun): 'success' | 'danger' | 'warn' | 'info' {
        const status = this.statusOf(run);
        return status === 'Completed' ? 'success'
            : status === 'Failed' ? 'danger'
            : status === 'Stalled' ? 'warn'
            : 'info';
    }

    ingest(): void {
        this.ingesting.set(true);
        this.ingestResult.set(null);
        this.ingestError.set(null);
        this.api.ingestOhlqCatalog().subscribe({
            next: (r) => {
                this.ingestResult.set(r);
                this.ingesting.set(false);
                this.reload();
                this.loadCategories();
                this.loadSummary();
                if (this.activeTab() === 'syncs') this.refreshSyncs();
            },
            error: (e) => {
                this.ingestError.set(e?.error?.detail || e?.message || 'The OHLQ read failed.');
                this.ingesting.set(false);
            }
        });
    }

    /// Opens a product from the list: a different product entirely, so the previous one is cleared rather
    /// than left on screen under the new title.
    openById(itemId: string): void {
        this.detail.set(null);
        this.timeline.set([]);
        this.loadDetail(itemId, true);
    }

    /// Switches to another size of the SAME product. The panel keeps its content while the next size
    /// loads — blanking it made every size click flash the whole dialog, and the two sizes differ by a
    /// price and a barcode, not by a screen.
    switchSize(itemId: string): void {
        if (itemId === this.selectedItemId()) return;
        this.loadDetail(itemId, false);
    }

    private loadDetail(itemId: string, blocking: boolean): void {
        // Set FIRST so the size picker highlights the click immediately, rather than waiting for the
        // response to tell it what it already knows.
        this.selectedItemId.set(itemId);
        this.showDetail = true;
        this.detailError.set(null);
        if (blocking) this.detailLoading.set(true); else this.switching.set(true);

        this.api.getOhlqItem(itemId).subscribe({
            next: (d) => {
                // A stale response from a size the user has already clicked past must not overwrite the
                // one they are looking at now.
                if (this.selectedItemId() !== d.itemId) return;
                this.detail.set(d);
                this.timeline.set(visibleOhlqTimeline(d.timeline));
                this.detailLoading.set(false);
                this.switching.set(false);
            },
            error: (e) => {
                this.detailError.set(e?.error?.detail || e?.message || 'Could not load this item.');
                this.detailLoading.set(false);
                this.switching.set(false);
            }
        });
    }

    closeDetail(): void {
        this.showDetail = false;
        this.selectedItemId.set(null);
        this.detail.set(null);
        this.timeline.set([]);
    }

    describe(e: OhlqTimelineEntry): string {
        return describeOhlqEvent(e);
    }

    coverageLabel(coverage: string): string {
        return this.coverageOptions.find((o) => o.value === coverage)?.label ?? coverage;
    }
}
